import { useEffect, useMemo, useRef } from 'react';
import { Layout } from '../components/Layout';

/**
 * LegacyHtmlPage — renders an existing static HTML page (imported at build time
 * via Vite's `?raw`) inline as React content, inside the shared Layout.
 *
 * The component:
 *   - Extracts the original <style> and <body> from the raw HTML.
 *   - Strips the legacy <nav class="sitenav">, its inline theme/print script,
 *     and the legacy <footer> (the React Layout provides these).
 *   - Injects style + body into the page via dangerouslySetInnerHTML so that
 *     all content, math, and SVGs render natively — no iframe.
 *   - Calls MathJax.typesetPromise() after mount so math expressions render.
 *
 * The legacy CSS is preserved as-is. Because each page's <style> and the
 * React Layout share the same CSS-variable token names (--paper, --ink,
 * --accent, ...), there's no visual conflict.
 */
function extract(html) {
  // Pull out every <style> block — pages may have several.
  const styles = [];
  html.replace(/<style[^>]*>([\s\S]*?)<\/style>/g, (_, css) => {
    styles.push(css);
    return '';
  });

  // Pull out the <body> contents.
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);
  let body = bodyMatch ? bodyMatch[1] : html;

  // Strip the legacy site nav + its inline IIFE.
  body = body.replace(/<nav class="sitenav"[\s\S]*?<\/nav>/g, '');
  body = body.replace(
    /<script>\s*\(function\(\)\{[^}]*fn-theme[\s\S]*?\}\)\(\);\s*<\/script>/g,
    '',
  );
  // Strip any remaining inline scripts (search wiring, theme toggles, etc.).
  body = body.replace(/<script[^>]*>[\s\S]*?<\/script>/g, '');
  // Strip the legacy footer (Layout provides one).
  body = body.replace(/<footer[\s\S]*?<\/footer>/g, '');

  return { style: styles.join('\n'), body };
}

export function LegacyHtmlPage({ slug, title, html }) {
  const { style, body } = useMemo(() => extract(html), [html]);
  const containerRef = useRef(null);

  // Re-render math after the body is mounted.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    // MathJax — most pages use $...$ inline / $$...$$ display
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise([node]).catch(() => {});
    }
    // KaTeX auto-render — used by ps, rl
    if (window.renderMathInElement) {
      window.renderMathInElement(node, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$', right: '$', display: false },
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true },
        ],
        throwOnError: false,
      });
    }
  }, [body]);

  return (
    <Layout accent={slug} title={title}>
      {/* Inject legacy page CSS — scoped naturally by class names */}
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: body }} />
    </Layout>
  );
}
